// 권위:
//   - src/agents/trading/signals/signal_types.py::SignalKind (운영 DB raw enum, snake_case)
//   - src/agents/trading/execution/unfilled_rollover.py::UnfilledOrderStage (운영 DB raw enum)
//   - dashboard/api/.../TradingDtos.java (signalKind / stage = string passthrough)
//   - tradingMock.ts (UI friendly 타입 — UISignalKind 4종 / UIStage 5종)
//
// dashboard-api 는 DB raw 값을 그대로 응답한다 (DTO 주석 참조). 본 모듈은 raw 값을
// UI 친화 enum 으로 정규화한다. mock 시절의 타입 단순성을 유지하면서 운영 데이터를
// 손실 없이 매핑한다.

import type { PositionRow as UIPosition, UnfilledOrderRow as UIUnfilled, UnfilledStage } from './tradingMock';

export type UISignalKind = 'BUY' | 'SELL' | 'STOP_LOSS' | 'TAKE_PROFIT';

/**
 * SignalKind raw → UI 4종.
 *
 * 운영 매핑 (signal_types.py 권위):
 *   - buy_breakout / buy_reentry_05 / buy_rebreak_failed → BUY
 *   - sell_partial_tp1 / sell_partial_tp2 → TAKE_PROFIT
 *   - sell_half / sell_all (STOP_LOSS_KINDS) → STOP_LOSS
 *   - sell_ma20_exit / sell_override_50pct → SELL
 *
 * 이미 대문자 UI enum (BUY/SELL/STOP_LOSS/TAKE_PROFIT) 으로 들어오는 경우도 통과.
 * 미식별 raw 는 SELL 로 폴백 (보수적 — 운영 신규 enum 추가 시 매핑 누락 가시화).
 */
export function mapSignalKind(raw: string | null | undefined): UISignalKind {
  if (!raw) return 'SELL';
  const v = String(raw).toLowerCase();
  // 이미 UI enum (대문자) 으로 들어온 케이스
  if (v === 'buy' || v === 'sell' || v === 'stop_loss' || v === 'take_profit') {
    return v.toUpperCase() as UISignalKind;
  }
  if (v.startsWith('buy_')) return 'BUY';
  if (v === 'sell_half' || v === 'sell_all') return 'STOP_LOSS';
  if (v === 'sell_partial_tp1' || v === 'sell_partial_tp2') return 'TAKE_PROFIT';
  if (v.startsWith('sell_')) return 'SELL';
  return 'SELL';
}

/**
 * UnfilledOrderStage raw → UI stage 5종.
 *
 * 운영 매핑 (unfilled_rollover.py::UnfilledOrderStage 권위):
 *   - S1_PLACED                  → S1
 *   - S2_PENDING / S2_PLACED     → S2
 *   - S3_PENDING / S3_PLACED     → S3
 *   - S1_PENDING_ROLLOVER        → D2_OPEN (D+2 시초 재이월 대기)
 *   - DISCARDED                  → DISCARDED
 *   - COMPLETED                  → DISCARDED (UI 미표시 — repo 가 ACTIVE 필터링하므로 도달 불가지만 안전망)
 *
 * 이미 UI 표기 (S1/S2/S3/D2_OPEN/DISCARDED) 로 들어오는 경우도 통과.
 */
export function mapStage(raw: string | null | undefined): UnfilledStage {
  if (!raw) return 'DISCARDED';
  const v = String(raw).toUpperCase();
  // 이미 UI enum
  if (v === 'S1' || v === 'S2' || v === 'S3' || v === 'D2_OPEN' || v === 'DISCARDED') {
    return v as UnfilledStage;
  }
  if (v === 'S1_PLACED') return 'S1';
  if (v === 'S2_PENDING' || v === 'S2_PLACED') return 'S2';
  if (v === 'S3_PENDING' || v === 'S3_PLACED') return 'S3';
  if (v === 'S1_PENDING_ROLLOVER') return 'D2_OPEN';
  return 'DISCARDED';
}

// ── API row → UI row 변환 ─────────────────────────────────

/**
 * 백엔드 DTO 의 numeric 필드는 BigDecimal → JSON `number` 또는 `string` 둘 다 가능.
 * Jackson 기본은 number 직렬화이나 정밀도 issue 회피용 string 가능성 대비.
 *
 * `null` 입력 시 `null` 반환 (시세 source 부재 케이스).
 */
function toNum(v: unknown): number | null {
  if (v == null) return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/**
 * 백엔드 PositionRow → UI PositionRow.
 *
 * 시세 source 부재 시 currentPrice 는 백엔드가 avg_entry_price 폴백 또는 NULL.
 * 백엔드가 NULL 을 보내면 UI 측 currentPrice 도 NaN 으로 표시 (UI 분기 책임).
 *
 * tradingMock.ts 의 PositionRow.currentPrice 타입은 `number` 이지만 시세 부재 케이스
 * 표현을 위해 본 매퍼는 `number` (NaN 가능) 으로 반환하고, page 컴포넌트가 NaN 분기로
 * "-" + tooltip 처리한다. 타입 시그니처 변경은 회귀 영향 최소화 차원에서 회피.
 */
export interface ApiPositionRow {
  symbol: string;
  symbolName: string;
  state: string;
  qty: number;
  avgEntry: number | string;
  currentPrice: number | string | null;
  stopLossPrice: number | string;
  tp1Price: number | string | null;
  tp1Done: boolean;
  tp2Price: number | string | null;
  tp2Done: boolean;
  trailStopPrice: number | string | null;
  lastCorpActionRecalcAt: string | null;
  enteredAt: string;
}

export function toUiPosition(api: ApiPositionRow): UIPosition {
  const current = toNum(api.currentPrice);
  return {
    symbol: api.symbol,
    symbolName: api.symbolName ?? api.symbol,
    state: api.state as UIPosition['state'],
    qty: api.qty,
    avgEntry: toNum(api.avgEntry) ?? 0,
    // 시세 부재 표현: NaN (page 가 isNaN 분기로 "-" 표시)
    currentPrice: current ?? Number.NaN,
    stopLossPrice: toNum(api.stopLossPrice) ?? 0,
    tp1Price: toNum(api.tp1Price),
    tp1Done: api.tp1Done,
    tp2Price: toNum(api.tp2Price),
    tp2Done: api.tp2Done,
    trailStopPrice: toNum(api.trailStopPrice),
    lastCorpActionRecalcAt: api.lastCorpActionRecalcAt,
    enteredAt: api.enteredAt,
  };
}

export interface ApiUnfilledRow {
  rowId: string;
  symbol: string;
  symbolName: string;
  signalKind: string;
  unfilledQty: number;
  stage: string;
  rolloverCount: number;
  nextActionAt: string;
  createdAt: string;
}

export function toUiUnfilled(api: ApiUnfilledRow): UIUnfilled {
  return {
    rowId: api.rowId,
    symbol: api.symbol,
    symbolName: api.symbolName ?? api.symbol,
    signalKind: mapSignalKind(api.signalKind),
    unfilledQty: api.unfilledQty,
    stage: mapStage(api.stage),
    rolloverCount: api.rolloverCount,
    nextActionAt: api.nextActionAt,
    createdAt: api.createdAt,
  };
}
